import { useEffect, useState } from "react";
import { EyeOff, Eye, Trash2, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin dashboard for reader comments across every book.
 * - Hide (soft) or delete (hard) with one click.
 * - Filter by book title or hidden state.
 */
export default function AdminComments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all"); // all | visible | hidden

  const load = () => {
    setLoading(true);
    api.get("/admin/comments")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setHidden = async (row, hidden) => {
    setBusy(row.id);
    try {
      await api.patch(`/comments/${row.id}`, { hidden });
      toast.success(hidden ? "Comment hidden from readers." : "Comment restored.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update comment.");
    } finally { setBusy(null); }
  };

  const del = async (row) => {
    if (!window.confirm(`Permanently delete this comment from ${row.user_name || "reader"}?`)) return;
    setBusy(row.id);
    try {
      await api.delete(`/comments/${row.id}`);
      toast.success("Comment deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't delete comment.");
    } finally { setBusy(null); }
  };

  const visible = rows.filter((r) => {
    if (filter === "visible") return !r.hidden;
    if (filter === "hidden") return r.hidden;
    return true;
  });
  const hiddenCount = rows.filter((r) => r.hidden).length;

  return (
    <div className="max-w-5xl" data-testid="admin-comments-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-fredoka text-3xl" style={{ color: "#2D3055" }}>Reader Comments</h1>
          <p className="font-nunito text-sm mt-1" style={{ color: "#7E82A1" }}>
            Keep the tone kind — hide, restore, or delete anything that doesn't feel right.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {["all", "visible", "hidden"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className="px-3 py-1.5 rounded-full font-fredoka text-xs"
              style={{
                background: filter === f ? "#FF6B9D" : "#FFFFFF",
                color: filter === f ? "#FFFFFF" : "#2D3055",
                border: `1.5px solid ${filter === f ? "#FF6B9D" : "#E4DFF6"}`,
              }}
              data-testid={`admin-comments-filter-${f}`}
            >
              {f === "all" ? `All (${rows.length})` : f === "visible" ? `Visible (${rows.length - hiddenCount})` : `Hidden (${hiddenCount})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="font-nunito" style={{ color: "#7E82A1" }}>Loading comments…</p>
      ) : visible.length === 0 ? (
        <div className="paper-card p-8 text-center" data-testid="admin-comments-empty">
          <MessageCircle size={28} style={{ color: "#7E82A1" }} className="mx-auto" />
          <p className="mt-3 font-fredoka text-xl" style={{ color: "#2D3055" }}>Nothing to see here.</p>
          <p className="mt-1 font-nunito" style={{ color: "#4A4E6D" }}>
            {filter === "hidden" ? "No hidden comments." : "No comments yet — quiet little corner."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((c) => {
            const when = c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
            const rowBusy = busy === c.id;
            return (
              <li
                key={c.id}
                className="paper-card p-4"
                style={{ opacity: c.hidden ? 0.55 : 1 }}
                data-testid={`admin-comment-${c.id}`}
              >
                <div className="flex gap-3">
                  {c.user_picture ? (
                    <img src={c.user_picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="w-10 h-10 rounded-full flex items-center justify-center font-fredoka text-sm shrink-0"
                          style={{ background: "#C7B5FF", color: "#2D3055" }}>
                      {(c.user_name || "R").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-fredoka" style={{ color: "#2D3055" }}>{c.user_name || "Reader"}</span>
                      <span className="text-xs font-nunito" style={{ color: "#7E82A1" }}>{when}</span>
                      <span className="text-xs font-caveat" style={{ color: "#FF6B9D" }}>· on {c.book_title}</span>
                      {c.hidden && (
                        <span className="text-xs font-fredoka px-2 py-0.5 rounded-full"
                              style={{ background: "#FFE1EC", color: "#D6407E" }}>
                          hidden
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-nunito text-sm whitespace-pre-wrap" style={{ color: "#3D405B" }}>
                      {c.text}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      {c.hidden ? (
                        <button
                          type="button"
                          onClick={() => setHidden(c, false)}
                          disabled={rowBusy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-fredoka text-xs"
                          style={{ background: "#FFFFFF", color: "#3FB07B", border: "1.5px solid #A9E8C4" }}
                          data-testid={`admin-comment-restore-${c.id}`}
                        >
                          {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setHidden(c, true)}
                          disabled={rowBusy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-fredoka text-xs"
                          style={{ background: "#FFFFFF", color: "#7E82A1", border: "1.5px solid #E4DFF6" }}
                          data-testid={`admin-comment-hide-${c.id}`}
                        >
                          {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />} Hide
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => del(c)}
                        disabled={rowBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-fredoka text-xs"
                        style={{ background: "#FFFFFF", color: "#D6407E", border: "1.5px solid #F6ACC7" }}
                        data-testid={`admin-comment-delete-${c.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
